import { useCallback, useEffect, useRef } from 'react';

/**
 * DP95 Part 1 — THE BAND SHRINKS AS YOU SCROLL.
 *
 * The scroller is the PANEL (DP93 Part 1d), not the window, so that is what this
 * listens to. The band is taken OUT of the flex column and laid over the top of
 * the content region, and the panel is given a top padding equal to the band's
 * current height. That is deliberate and it is DP95 ruling 4b: the panel's own
 * box must not change as the band shrinks, so the band cannot be sharing the
 * column's height with it.
 *
 * DP97 ruling 1 — THE CLOSE IS SLOWER, AND IT EASES.
 * DP95 took a pixel off the band for every pixel scrolled, which meant the
 * PANEL's content travelled at 2x the scroll (the scroll itself, plus the top
 * padding shrinking under it) and the band ran ahead of the hand. Two changes:
 *   - RATE 0.5: the band gives up half a pixel per pixel scrolled, so its full
 *     travel takes twice its own height in scroll distance.
 *   - a smoothstep on the way down: slow to start, slow to settle, so the last
 *     part of the close arrives rather than snapping.
 * Content travel is now 1:1 at the top and at the floor, peaking at 1.75x in the
 * middle instead of a flat 2x. OPENING AT DEPTH IS UNCHANGED AND IMMEDIATE: the
 * easing is in the scroll-to-height mapping, not a CSS transition, so a click
 * jumps straight to full height with nothing to animate through.
 * Nothing is cut off: the band's contents scale with the band's own height ratio
 * and fade out, so the type shrinks away instead of being clipped by the slot's
 * edge.
 *
 * No React state and no re-render per scroll event: the handler writes four CSS
 * custom properties inside one requestAnimationFrame, and a data attribute only
 * when the state actually crosses a threshold. That is how the jitter in DP95
 * Part 1f is avoided; the worst frame it costs is left on
 * `window.__kmMastFrameMs` so it can be read rather than claimed.
 */
const FLOOR = 48;          // DP95 Part 1b — the floor, in pixels
const DESKTOP_MIN = 640;   // below this the band scrolls away entirely (DP94)
const RATE = 0.5;          // DP97 ruling 1 — px of band per px of scroll
// smoothstep: p²(3−2p). Zero slope at both ends, so the close starts gently and
// settles into the floor instead of arriving at speed.
const ease = (p) => p * p * (3 - 2 * p);

export function useMastheadShrink() {
  const contentRef = useRef(null);
  const mastRef = useRef(null);
  const panelRef = useRef(null);
  const fullRef = useRef(0);
  const pinnedRef = useRef(false);
  // null, not 'full': the first write MUST set the attribute, because the
  // attribute is what switches the band from the flex column to the overlay.
  // Measured with 'full' here: at scroll 0 the panel was 470px tall and moved to
  // 864 the moment the band shrank a pixel — ruling 4b broken by an initial value.
  const stateRef = useRef(null);
  const queuedRef = useRef(false);

  const write = useCallback(() => {
    queuedRef.current = false;
    const content = contentRef.current;
    const mast = mastRef.current;
    const panel = panelRef.current;
    if (!content || !mast || !panel) return;
    const t0 = performance.now();

    // NOT firstElementChild: the collapsed bar is a static child of the slot and
    // the portal appends the band AFTER it, so the first child is the 48px bar.
    // Measured that way the band's "full height" came out as 48 and it never
    // opened. Anything in the slot that is not the bar IS the band.
    const band = mast.querySelector(':scope > :not(.km-shell__mastbar)');
    const desktop = window.innerWidth >= DESKTOP_MIN;
    if (!band || !desktop) {
      // No band on this page, or a phone: the slot is back in the flow and the
      // panel takes no padding from us.
      content.style.removeProperty('--km-mast-h');
      content.style.removeProperty('--km-mast-s');
      content.style.removeProperty('--km-mast-p');
      content.style.removeProperty('--km-mast-pad');
      content.removeAttribute('data-mast');
      return;
    }

    const full = fullRef.current || band.offsetHeight;
    fullRef.current = full;
    const travel = Math.max(1, full - FLOOR);
    const linear = Math.min(1, Math.max(0, (panel.scrollTop * RATE) / travel));
    const h = pinnedRef.current ? full : full - ease(linear) * travel;
    const p = (full - h) / travel;

    content.style.setProperty('--km-mast-h', `${Math.round(h)}px`);
    content.style.setProperty('--km-mast-s', String(Math.round((h / full) * 1000) / 1000));
    content.style.setProperty('--km-mast-p', String(Math.round(p * 1000) / 1000));
    // the band overlays the content region's top; the panel starts one mat down
    content.style.setProperty('--km-mast-pad', `${Math.max(0, Math.round(h) - 24)}px`);

    const next = pinnedRef.current ? 'open'
      : h <= FLOOR + 0.5 ? 'floor'
        : p > 0 ? 'shrinking' : 'full';
    if (next !== stateRef.current) {
      stateRef.current = next;
      content.setAttribute('data-mast', next);
    }
    const ms = performance.now() - t0;
    const f = window.__kmMastFrames || (window.__kmMastFrames = { n: 0, total: 0, max: 0 });
    f.n += 1;
    f.total = Math.round((f.total + ms) * 100) / 100;
    f.max = Math.max(f.max, Math.round(ms * 100) / 100);
    f.avg = Math.round((f.total / f.n) * 1000) / 1000;
    window.__kmMastFrameMs = f.max;
  }, []);

  const schedule = useCallback(() => {
    if (queuedRef.current) return;
    queuedRef.current = true;
    requestAnimationFrame(write);
  }, [write]);

  // Clicking the collapsed bar opens the band AT ANY DEPTH; the next scroll
  // re-collapses it, because the height is a function of scrollTop again.
  // The panel takes focus so the keyboard can carry on scrolling — after a click
  // the focus was left on the bar and PageDown did nothing.
  const openBand = useCallback(() => {
    pinnedRef.current = true;
    fullRef.current = 0;
    schedule();
    const panel = panelRef.current;
    if (panel) panel.focus({ preventScroll: true });
  }, [schedule]);

  useEffect(() => {
    const panel = panelRef.current;
    const mast = mastRef.current;
    if (!panel || !mast) return undefined;

    const onScroll = () => {
      if (pinnedRef.current) pinnedRef.current = false;
      schedule();
    };
    panel.addEventListener('scroll', onScroll, { passive: true });

    // THE BAND IS AN OVERLAY OVER THE SCROLLER, so a wheel over the band found no
    // scrollable ancestor and was silently dropped: on My Desk that was a
    // 1688x394 dead region — 36% of the content region — where the most natural
    // gesture in the whole feature did nothing. The band forwards it to the panel.
    const onWheel = (event) => {
      if (window.innerWidth < DESKTOP_MIN) return;
      // any wheel over the band counts as "scrolling again", so a band opened
      // from the bar re-collapses even when the panel is already at its limit
      pinnedRef.current = false;
      const before = panel.scrollTop;
      panel.scrollTop = before + event.deltaY;
      if (panel.scrollTop !== before) event.preventDefault();
      schedule();
    };
    mast.addEventListener('wheel', onWheel, { passive: false });

    // The band's natural height changes with the width, with the tenant's image
    // and with the figures, so it is measured rather than assumed — and only
    // when it actually changes, never per scroll event.
    const ro = new ResizeObserver(() => {
      const band = mast.querySelector(':scope > :not(.km-shell__mastbar)');
      fullRef.current = band ? band.offsetHeight : 0;
      schedule();
    });
    // the BAND, not the slot: the slot's height is what this hook writes, so
    // observing it would have the observer chasing its own output.
    const first = mast.querySelector(':scope > :not(.km-shell__mastbar)');
    if (first) ro.observe(first);
    if (contentRef.current) ro.observe(contentRef.current);

    // the portal can land the band after this effect runs
    const mo = new MutationObserver(() => {
      const band = mast.querySelector(':scope > :not(.km-shell__mastbar)');
      fullRef.current = band ? band.offsetHeight : 0;
      if (band) ro.observe(band);
      schedule();
    });
    mo.observe(mast, { childList: true });

    window.addEventListener('resize', schedule);
    schedule();
    return () => {
      panel.removeEventListener('scroll', onScroll);
      mast.removeEventListener('wheel', onWheel);
      window.removeEventListener('resize', schedule);
      ro.disconnect();
      mo.disconnect();
    };
  }, [schedule]);

  return { contentRef, mastRef, panelRef, openBand };
}

export default useMastheadShrink;
